import React from 'react';
import { Loader2, Upload, Check, Sparkles } from 'lucide-react';

const LoginScreen = ({ handleGoogleLogin, loading, user, fileInputRef, isProcessing, credits }) => {
  return (
    <div className="min-h-[calc(100vh-200px)]">
      {/* Animated Top Banner */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-3 mb-8 overflow-hidden">
        <div className="flex animate-marquee">
          <div className="flex-shrink-0 flex items-center">
            <span className="text-base font-medium px-8">
              ✨ AI-Powered Invoice Processing • No Expensive Tools Required • Just 10¢ per Invoice
            </span>
            <span className="text-base font-medium px-8">
              🚀 Try FREE with 10 Credits • No Risk • Process Invoices in Seconds
            </span>
            <span className="text-base font-medium px-8">
              💰 Save Money & Time • 100% Secure • No Data Access Required
            </span>
          </div>
          <div className="flex-shrink-0 flex items-center">
            <span className="text-base font-medium px-8">
              ✨ AI-Powered Invoice Processing • No Expensive Tools Required • Just 10¢ per Invoice
            </span>
            <span className="text-base font-medium px-8">
              🚀 Try FREE with 10 Credits • No Risk • Process Invoices in Seconds
            </span>
            <span className="text-base font-medium px-8">
              💰 Save Money & Time • 100% Secure • No Data Access Required
            </span>
          </div>
        </div>
      </div>

      {/* Split Screen Layout */}
      <div className="grid grid-cols-2 gap-12 items-center">
        
        {/* LEFT SIDE - Benefits & Demo */}
        <div className="space-y-6">
          <div>
            <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Invoice Processing Reimagined
            </h1>
            <div className="flex items-center gap-3 mb-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full font-bold shadow-lg">
                <Check className="w-5 h-5" />
                QuickBooks®
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full font-bold shadow-lg">
                <Check className="w-5 h-5" />
                Coupa®
              </div>
            </div>
            <p className="text-lg text-gray-600 mb-2 font-semibold">
              Compatible Output Formats for Both Systems
            </p>
            <p className="text-xl text-gray-600 mb-2">
              Why spend <span className="line-through text-red-500">$1000s</span> on complex tools when AI can do it for just <span className="font-bold text-green-600">10¢</span> per invoice?
            </p>
          </div>

          {/* Key Benefits */}
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-4 bg-green-50 rounded-xl border border-green-200 transform hover:scale-105 transition-transform">
              <div className="bg-green-500 rounded-full p-1.5 mt-0.5 flex-shrink-0">
                <Check className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="font-semibold text-green-900">Only 10¢ per Invoice</p>
                <p className="text-sm text-green-700">No monthly fees, no hidden costs. Pay as you go.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-200 transform hover:scale-105 transition-transform">
              <div className="bg-blue-500 rounded-full p-1.5 mt-0.5 flex-shrink-0">
                <Check className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="font-semibold text-blue-900">100% Secure & Private</p>
                <p className="text-sm text-blue-700">PDFs encrypted, processed, and immediately deleted. Zero data storage.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-xl border border-purple-200 transform hover:scale-105 transition-transform">
              <div className="bg-purple-500 rounded-full p-1.5 mt-0.5 flex-shrink-0">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="font-semibold text-purple-900">AI-Powered Accuracy</p>
                <p className="text-sm text-purple-700">AI-powered invoice data extraction with industry-leading accuracy.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200 transform hover:scale-105 transition-transform">
              <div className="bg-amber-500 rounded-full p-1.5 mt-0.5 flex-shrink-0">
                <Check className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="font-semibold text-amber-900">No Access Required</p>
                <p className="text-sm text-amber-700">No permissions, no system access. Just upload and download.</p>
              </div>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap gap-3 pt-4">
            <div className="px-3 py-2 bg-gray-100 rounded-lg border border-gray-200 text-xs font-medium text-gray-700">
              🔒 Bank-Level Encryption
            </div>
            <div className="px-3 py-2 bg-gray-100 rounded-lg border border-gray-200 text-xs font-medium text-gray-700">
              ✅ GDPR Compliant
            </div>
            <div className="px-3 py-2 bg-gray-100 rounded-lg border border-gray-200 text-xs font-medium text-gray-700">
              🛡️ SOC 2 Type II
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - Login Box */}
        <div className="flex items-center justify-center">
          <div className="relative w-full max-w-md">
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl blur-2xl opacity-20 animate-pulse"></div>
            <div className="relative bg-white rounded-3xl shadow-2xl p-10 border border-gray-100">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center">
                <Upload className="w-10 h-10 text-indigo-600" />
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">Get Started Free</h2>
              <p className="text-gray-600 mb-6 text-center text-sm">
                Sign in to process your first invoice
              </p>
              
              {!user ? (
                <>
                  {/* Google Sign In Button */}
                  <button
                    onClick={handleGoogleLogin}
                    disabled={loading}
                    className="w-full bg-white border-2 border-gray-300 text-gray-700 py-4 px-4 rounded-xl font-semibold hover:bg-gray-50 hover:border-indigo-400 transition-all flex items-center justify-center gap-3 mb-5 shadow-md hover:shadow-lg"
                  >
                    {loading ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      <>
                        <svg className="w-6 h-6" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continue with Google
                      </>
                    )}
                  </button>

                  {/* Free Credits Offer */}
                  <div className="p-5 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-xl text-white mb-4 shadow-lg">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <div className="bg-white rounded-full p-1">
                        <Sparkles className="w-5 h-5 text-indigo-600" />
                      </div>
                      <p className="text-lg font-bold">10 FREE Credits</p>
                    </div>
                    <p className="text-center text-sm text-white/90 mb-2">
                      Process 10 invoices absolutely free!
                    </p>
                    <p className="text-center text-xs text-white/80 leading-relaxed">
                      <span className="font-semibold">Loved it? Congrats, you just discovered the easiest way to save money! 💰</span>
                    </p>
                  </div>

                  {/* Trust Message */}
                  <div className="text-center">
                    <p className="text-xs text-gray-400 italic leading-relaxed">
                      "In this AI era, invoice processing doesn't need big expensive tools anymore. Give it a try!"
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <button
                    onClick={() => fileInputRef.current.click()}
                    disabled={isProcessing}
                    className="px-8 py-3 w-full rounded-xl font-medium transition-all bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? 'Processing...' : 'Choose PDF File (1 Credit)'}
                  </button>
                  {credits > 0 && (
                    <p className="text-sm text-gray-600 mt-4 text-center">
                      You have <span className="font-bold text-indigo-600">{credits} credits</span> remaining
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;